import React, { Component } from 'react';

import { translate } from 'components/translate';
import cloneDeep from 'lodash/cloneDeep';
import deburr from 'lodash/deburr';
import { generateId } from 'modules/content/helper';
import PropTypes from 'prop-types';

import { INITIAL_STATE } from './constants';
import ContentChecklist from './ContentChecklist';
import PagesChecklist from './PagesChecklist';
import PagesContentOptions from './PagesContentOptions';
import PagesContentService from './PagesContentService';

import './PagesContent.scss';
import '../access/AccessSelectDate.scss';

@translate()
export default class PagesContent extends Component {
  checklistId = generateId();

  static propTypes = {
    t: PropTypes.func,
    field: PropTypes.string,
    incompleteField: PropTypes.string,
    fieldData: PropTypes.object,
    items: PropTypes.array,
    onMenuOpen: PropTypes.func,
    setValue: PropTypes.func,
    setClearFunction: PropTypes.func
  };

  state = {
    ...INITIAL_STATE,
    items: []
  };

  clearState = () => {
    this.clearAllCheckedItems();
    this.setState({ ...INITIAL_STATE });
  };

  componentDidMount() {
    const { setClearFunction } = this.props;

    setClearFunction && setClearFunction(this.clearState);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.items.length !== prevState.items.length) {
      const itemsFiltered = nextProps.items.map((item) => {
        item.pages = item.pages.map((page) => ({
          ...page,
          show: true
        }));
        item.show = true;

        return item;
      });

      return { items: itemsFiltered };
    }

    if (PagesContentService.hasPagesChanged(nextProps, prevState)) {
      const { completed, incompleted } = nextProps.fieldData;
      const { itemsFormatted, selectedItemsFiltered, concludedValue } =
        PagesContentService.getItemsFormatted(
          nextProps.items,
          completed,
          PagesContentService.concludedOptions(nextProps.t)
        );

      return {
        items: nextProps.items,
        itemsFormatted,
        selectedItemsFiltered,
        concludedValue,
        completedDataIds: completed.ids,
        incompletedDataIds: incompleted.ids
      };
    }

    return null;
  }

  setValue(ids = [], completed) {
    const { field, incompleteField, setValue } = this.props;
    const value = {
      completed,
      ids
    };

    setValue(value, completed ? field : incompleteField);
  }

  clearAllCheckedItems() {
    const { items } = this.state;

    const itemsFiltered = items.map((item) => {
      item.checked = false;

      item.pages = item.pages.map((page) => ({
        ...page,
        checked: false
      }));

      return item;
    });

    this.setState({ items: itemsFiltered });
  }

  clearAllSelectedFields() {
    const selectedItemsFiltered = [];

    this.clearAllCheckedItems();

    this.setState({ selectedItemsFiltered });

    this.setValue([], true);
    this.setValue([], false);
  }

  changeFilter(searchedValue) {
    const { items } = this.props;

    const regexSearchedValue = new RegExp(deburr(searchedValue), 'i');
    const itemsFiltered = PagesContentService.showItemsFiltered(
      items,
      regexSearchedValue
    );

    this.setState({ items: itemsFiltered });
  }

  onChange(event) {
    const { items } = this.state;
    const { value } = event.target;

    const itemsFiltered = PagesContentService.checkItemsFiltered(items, value);

    this.setState({ item: itemsFiltered });
  }

  groupPagesFilterByStatus(pageFilters) {
    const pageFiltersIncomplete = [];
    const pageFiltersComplete = pageFilters.filter((item) => {
      if (item.concluded.value === 'CONCLUDED') {
        return item;
      } else {
        pageFiltersIncomplete.push(item);
        return null;
      }
    });

    return { pageFiltersIncomplete, pageFiltersComplete };
  }

  removeItem(itemToRemove) {
    const { selectedItemsFiltered, items } = this.state;

    const itemsUpdated = PagesContentService.getItemsUpdatedNonChecked(
      items,
      itemToRemove
    );
    const newSelectedItemsFiltered =
      PagesContentService.getSelectedItemsValueRemoved(
        selectedItemsFiltered,
        itemToRemove
      );

    this.setState({
      selectedItemsFiltered: newSelectedItemsFiltered,
      items: itemsUpdated
    });

    const { pageFiltersComplete, pageFiltersIncomplete } =
      this.groupPagesFilterByStatus(newSelectedItemsFiltered);

    this.setValue(
      PagesContentService.filterItemsByValue(pageFiltersComplete),
      true
    );
    this.setValue(
      PagesContentService.filterItemsByValue(pageFiltersIncomplete),
      false
    );
  }

  onMenuOpen(_, state) {
    const openConcludedOption =
      typeof state === 'undefined' ? !this.state.openConcludedOption : state;

    this.setState({ openConcludedOption });
  }

  setConcluded(value) {
    this.setState({ concludedValue: value });
  }

  replaceItem(
    completedIds,
    incompletedIds,
    selectedItems,
    wasAlreadyCompleteIdx,
    checkedPage
  ) {
    let newCompletedIds = cloneDeep(completedIds);
    let newIncompletedIds = cloneDeep(incompletedIds);

    if (wasAlreadyCompleteIdx >= 0) {
      newCompletedIds = completedIds.filter((id) => id !== checkedPage.id);
    } else {
      newIncompletedIds = incompletedIds.filter((id) => id !== checkedPage.id);
    }

    const newSelectedItemsFiltered = selectedItems.map((item) => {
      if (checkedPage.id === item.id) {
        return checkedPage;
      }
      return item;
    });

    return {
      completedIds: newCompletedIds,
      incompletedIds: newIncompletedIds,
      newSelectedItemsFiltered
    };
  }

  getItemIds(selectedItemsFiltered, onSelect) {
    return selectedItemsFiltered
      .filter((item) => onSelect(item))
      .map((item) => item.id);
  }

  isItemConcluded(item) {
    return item.concluded && item.concluded.value === 'CONCLUDED';
  }

  confirmSelectedItems() {
    const { items, concludedValue, selectedItemsFiltered } = this.state;
    let completedIds = this.getItemIds(selectedItemsFiltered, (item) =>
      this.isItemConcluded(item)
    );
    let incompletedIds = this.getItemIds(
      selectedItemsFiltered,
      (item) => !this.isItemConcluded(item)
    );
    let newSelectedItemsFiltered = cloneDeep(selectedItemsFiltered);

    items.forEach((item) => {
      item.pages.forEach((page) => {
        if (page.checked) {
          const checkedPage = { ...page, concluded: concludedValue };
          const wasAlreadyCompleteIdx = completedIds.indexOf(page.id);
          const wasAlreadyIncompleteIdx = incompletedIds.indexOf(page.id);
          const shouldReplaceItem =
            wasAlreadyCompleteIdx >= 0 || wasAlreadyIncompleteIdx >= 0;

          if (shouldReplaceItem) {
            ({ completedIds, incompletedIds, newSelectedItemsFiltered } =
              this.replaceItem(
                completedIds,
                incompletedIds,
                newSelectedItemsFiltered,
                wasAlreadyCompleteIdx,
                checkedPage
              ));
          } else {
            newSelectedItemsFiltered.push(checkedPage);
          }

          if (this.isItemConcluded(checkedPage)) {
            completedIds.push(page.id);
          } else {
            incompletedIds.push(page.id);
          }
        }
      });
    });

    this.clearAllCheckedItems();
    this.setState({ selectedItemsFiltered: newSelectedItemsFiltered });

    this.setValue(completedIds, true);
    this.setValue(incompletedIds, false);
  }

  get isButtonDisabled() {
    const { items, concludedValue } = this.state;

    const isSomeItemChecked = items.find((item) => {
      if (item.checked) {
        return true;
      }

      return item.pages.find((page) => page.checked);
    });

    const isConcludedValueSet = concludedValue && concludedValue.value;

    return !(isSomeItemChecked && isConcludedValueSet);
  }

  render() {
    const { field, onMenuOpen, fieldData } = this.props;
    const { items, selectedItemsFiltered } = this.state;

    return (
      <ContentChecklist
        field={field}
        onMenuOpen={onMenuOpen}
        fieldData={fieldData}
        clearAll={this.clearAllSelectedFields.bind(this)}
        items={items}
        changeFilter={this.changeFilter.bind(this)}
        removeItem={this.removeItem.bind(this)}
        selectedItems={selectedItemsFiltered}
        placeholderSelect="users_manager.deep_search.content.page_placeholder"
        placeholderSearchInput="users_manager.deep_search.content.quiz_page_score_search_placeholder"
        showRange={false}
      >
        <PagesChecklist
          items={items}
          checklistId={this.checklistId}
          className="subtitle__translation__checklist page__checklist__options"
          onChange={this.onChange.bind(this)}
        />

        <PagesContentOptions
          clearAllCheckedItems={this.clearAllCheckedItems.bind(this)}
          onMenuOpen={this.onMenuOpen.bind(this)}
          concludedValue={this.state.concludedValue}
          setConcluded={this.setConcluded.bind(this)}
          confirmSelectedItems={this.confirmSelectedItems.bind(this)}
          isButtonDisabled={this.isButtonDisabled}
          isOpen={this.state.openConcludedOption}
        />
      </ContentChecklist>
    );
  }
}
